import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, CreditCard, Receipt, LayoutGrid, TrendingUp, Activity } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function StatCard({ label, value, sub, icon: Icon, accent = "blue", testId }) {
  const ring = {
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-300",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    rose: "bg-rose-500/10 text-rose-600 dark:text-rose-300",
    violet: "bg-violet-500/10 text-violet-600 dark:text-violet-300",
    sky: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
  }[accent];
  return (
    <Card data-testid={testId} className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
      <CardContent className="p-5 flex items-start gap-4">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${ring}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold mt-0.5">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function fmtUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}
function fmtMXN(n) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
}

export default function AdminDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold">Resumen del sistema</h1>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const { users, subscriptions, revenue, content, signup_series } = data;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Resumen del sistema</h1>
        <p className="text-sm text-muted-foreground mt-1">Indicadores clave de Sprintroll en tiempo real.</p>
      </div>

      {/* Usuarios */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Usuarios</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="stat-users-total" label="Total" value={users.total} icon={Users} accent="blue" />
          <StatCard testId="stat-users-new7" label="Nuevos 7d" value={users.new_7d} icon={TrendingUp} accent="emerald" />
          <StatCard testId="stat-users-active24" label="Activos 24h" value={users.active_24h} icon={Activity} accent="amber" />
          <StatCard testId="stat-users-active7" label="Activos 7d" value={users.active_7d} icon={Activity} accent="sky" />
        </div>
      </section>

      {/* Ingresos */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Ingresos</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testId="stat-mrr-usd" label="MRR (USD)" value={fmtUSD(revenue.mrr_usd)} sub={`ARR ${fmtUSD(revenue.arr_usd)}`} icon={CreditCard} accent="emerald" />
          <StatCard testId="stat-mrr-mxn" label="MRR (MXN)" value={fmtMXN(revenue.mrr_mxn)} sub={`ARR ${fmtMXN(revenue.arr_mxn)}`} icon={CreditCard} accent="violet" />
          <StatCard testId="stat-paid-total" label="Pagos cobrados" value={revenue.paid_total} icon={Receipt} accent="blue" />
          <StatCard testId="stat-paid-30d" label="Pagos 30d" value={revenue.paid_30d} icon={Receipt} accent="rose" />
        </div>
      </section>

      {/* Suscripciones */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Suscripciones</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard testId="stat-sub-trial" label="En prueba" value={subscriptions.trialing} icon={CreditCard} accent="amber" />
          <StatCard testId="stat-sub-active" label="Activas" value={subscriptions.active} icon={CreditCard} accent="emerald" />
          <StatCard testId="stat-sub-pastdue" label="Vencidas/Past-due" value={subscriptions.past_due} icon={CreditCard} accent="rose" />
          <StatCard testId="stat-sub-canceled" label="Canceladas" value={subscriptions.canceled} icon={CreditCard} accent="violet" />
          <StatCard testId="stat-sub-expired" label="Expiradas" value={subscriptions.expired} icon={CreditCard} accent="blue" />
        </div>
      </section>

      {/* Contenido */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Contenido</h2>
        <div className="grid grid-cols-3 gap-4">
          <StatCard testId="stat-content-workspaces" label="Workspaces" value={content.workspaces} icon={LayoutGrid} accent="blue" />
          <StatCard testId="stat-content-boards" label="Boards" value={content.boards} icon={LayoutGrid} accent="violet" />
          <StatCard testId="stat-content-cards" label="Cards" value={content.cards} icon={LayoutGrid} accent="emerald" />
        </div>
      </section>

      {/* Signups chart */}
      <Card className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Registros últimos 14 días</CardTitle>
        </CardHeader>
        <CardContent className="h-64" data-testid="admin-signups-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={signup_series} margin={{ top: 10, right: 16, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="signups" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(120,120,120,0.15)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} stroke="rgba(120,120,120,0.5)" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="rgba(120,120,120,0.5)" />
              <Tooltip
                contentStyle={{ background: "rgba(15,23,42,0.9)", border: "none", borderRadius: 8, color: "white", fontSize: 12 }}
                labelStyle={{ color: "white" }}
              />
              <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#signups)" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
